import type {
  TriggerDefinition,
  Predicate,
  MetricKey,
} from "../../../../shared/triggers/types.ts";
import type { ScopeKind, OwnedRef } from "../../../../shared/triggers/scope.ts";
export type { TriggerDefinition, Predicate, MetricKey, ScopeKind, OwnedRef };
export interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  revision: number;
  definition: TriggerDefinition;
  assignment_count?: number;
  last_matched_at?: string | null;
}
export interface ScopeConfig {
  accountIds: string[];
  creativeIds: string[];
  campaignRefs: OwnedRef[];
  adRefs: OwnedRef[];
}
export interface Scope {
  id: string;
  name: string;
  kind: ScopeKind;
  version: number;
  config: ScopeConfig;
}
export interface BindingConfig {
  accountIds: string[] | null;
  excludedCampaigns: OwnedRef[];
  excludedAds: OwnedRef[];
}
export interface Assignment {
  id: string;
  trigger_id: string;
  scope_id: string;
  enabled: boolean;
  version: number;
  config: BindingConfig;
}
export interface BindingDraft {
  key: string;
  scope: Scope | null;
  newScope?: { name: string; kind: ScopeKind; config: ScopeConfig };
  enabled: boolean;
  config: BindingConfig;
}
export interface Account {
  socialAccountId?: string;
  socialAccountName?: string|null;
  id: string;
  name: string | null;
  currency: string;
  timezone_name: string | null;
  account_status: number | null;
}
export interface Candidate {
  id: string;
  adAccountId: string;
  type: "campaign" | "ad";
  name: string;
  configured_status: string;
  effective_status: string;
  parent_campaign_id: string | null;
  creative_id: string | null;
  needs_review: number;
  mapping_version: number;
  pending_candidate_id: string | null;
}
export interface Creative {
  accountIds?: string[];
  pageIds?: string[];
  geoCountries?: string[];
  socialAccounts?: {id:string;name:string|null}[];
  id: string;
  display_name: string;
  canonical_token: string;
  campaign_count: number;
}
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}
export interface Resolved {
  subjectKey: string;
  metrics: Partial<Record<MetricKey, string | null>>;
  reasons: string[];
  targets: {
    id: string;
    type: string;
    adAccountId: string;
    activeEpoch: number;
  }[];
  skippedTargets?: { id: string; adAccountId: string; reason: string }[];
  periodBucket: string;
  partitions?: {
    accountId: string;
    date: string;
    timezone: string;
    fetchedAt: string | null;
    fxManifest: unknown;
    actionMappingVersion: number;
    parserVersion: number;
  }[];
}
export interface Explanation {
  result: boolean | null;
  event: ExplainedPredicate;
  conditions: {
    operator: string;
    result: boolean | null;
    blocks: {
      id: string;
      operator: string;
      result: boolean | null;
      predicates: ExplainedPredicate[];
    }[];
  } | null;
}
export interface ExplainedPredicate {
  id: string;
  metric: MetricKey;
  operator: string;
  actual: string | null;
  comparedActual: string | null;
  comparedThreshold: string | null;
  result: boolean | null;
}
export interface Preview {
  mode: "dry_run";
  definitionHash: string;
  previewToken: string;
  scopeVersion: number;
  evaluatedAt: string;
  dataAsOf: string | null;
  periodLabel: string;
  snapshotIds: string[];
  counts: {
    subjects: number;
    matches: number;
    activeTargets: number;
    inactiveSkipped: number;
    unknown: number;
  };
  canActivate: boolean;
  reasons: string[];
  results: (Resolved & {
    result: boolean | null;
    explanation: Explanation;
    proposedActions: TriggerDefinition["actions"];
  })[];
  nextCursor: string | null;
}
export interface Execution {
  id: string;
  name: string;
  triggerId: string;
  revision: number;
  state: string;
  matchedAt: string;
  subjectKey: string;
  period: [string, string][];
  snapshotIds: string[];
  assignmentVersions: {
    id: string;
    version: number;
    scopeId: string;
    scopeVersion: number;
  }[];
  evidence: {
    metrics: Resolved["metrics"];
    actions: TriggerDefinition["actions"];
    explanation: Explanation;
    partitions?: Resolved["partitions"];
    targets?: Resolved["targets"];
  };
  contextSelections?: {
    target: { id: string; type: string; adAccountId: string };
    selectedAt: string;
  }[];
  tasks?: {
    id: string;
    state: string;
    version: number;
    entityType: string;
    entityId: string;
    adAccountId: string;
    reason: string | null;
    attempts: number;
    canRetry: boolean;
    canCancel: boolean;
    nextAttemptAt: number;
    expiresAt: number;
    triggerName: string;
    evidence?: {
      metrics: Resolved["metrics"];
      explanation: Explanation;
      snapshotIds: string[];
      evaluatedAt: string;
    };
    contributors: { match_id: string; assignment_id: string; valid: number }[];
  }[];
  attempts?: {
    id: string;
    task_id: string;
    phase: string;
    started_at: string;
    finished_at: string | null;
    outcome: string | null;
    response_code: number | null;
    error_code: number | null;
    details: { reason: string } | null;
  }[];
  confirmedDisable?:
    | {
        entity_id: string;
        trigger_name_snapshot: string;
        disabled_at: string;
      }[]
    | null;
  truncated?: boolean;
}
export interface QueueSummary {
  queueAvailable: boolean;
  paused: boolean;
  version: number;
  queued: number;
  running: number;
  retry: number;
  failed: number;
  uncertain: number;
  succeeded: number;
  lastHeartbeat: string | null;
  cooldownUntil: number;
  mutationsEnabled: boolean;
  evaluation: {
    pending: number;
    oldestPendingAt: string | null;
    lastCompletedAt: string | null;
  };
  selectedTargetEpochs: number;
}
export const levelLabel = {
  creative: "Крео (сумарно)",
  campaign: "Кампанія",
  ad: "Оголошення",
};
export const scopeLabel = {
  creative_set: "Список крео",
  all_in_accounts: "Список рекламних кабінетів",
  campaign_set: "Список кампаній",
  ad_set: "Список оголошень",
};
export const metricLabels: Record<MetricKey, string> = {
  spend: "Spend — витрати",
  impressions: "Покази",
  clicks: "Кліки",
  cpc: "CPC — ціна кліка",
  cpm: "CPM — ціна 1000 показів",
  ctr: "CTR",
  leads: "Ліди",
  cost_per_lead: "CPL — ціна ліда",
  purchases: "Покупки",
  cost_per_purchase: "Ціна покупки",
  registrations: "Реєстрації",
  cost_per_registration: "Ціна реєстрації",
};
export const operatorLabels = {
  eq: "= — дорівнює",
  gt: "> — більше",
  lt: "< — менше",
  gte: "≥ — більше або дорівнює",
  lte: "≤ — менше або дорівнює",
};
export const uid = () => crypto.randomUUID();
export const blankConfig = (): BindingConfig => ({
  accountIds: null,
  excludedCampaigns: [],
  excludedAds: [],
});
export const emptyScope = (): ScopeConfig => ({
  accountIds: [],
  creativeIds: [],
  campaignRefs: [],
  adRefs: [],
});
export const newPredicate = (): Predicate => ({
  id: uid(),
  metric: "spend",
  operator: "gte",
  value: "20",
});
export const newDefinition = (
  level: TriggerDefinition["metricLevel"] = "creative",
): TriggerDefinition => ({
  schemaVersion: 1,
  metricLevel: level,
  targetLevel: level === "creative" ? "campaign" : level,
  event: newPredicate(),
  conditions: null,
  actions: [{ type: "highlight", color: "#F59E0B" }],
  period: { kind: "today", timezoneMode: "ad_account" },
});
export const fmtDate = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString("uk-UA", {
        timeZone: "Europe/Kyiv",
        dateStyle: "short",
        timeStyle: "short",
      })
    : "Ще не перевірено";
export function reasonLabel(reason: string) {
  const key = reason.split(":")[0];
  const labels: Record<string, string> = {
    credentials_unavailable: "Токен недоступний",
    owner_unavailable: "Власник кабінету неактивний",
    rate_or_transient: "Meta обмежила запити; повтор заплановано",
    server_error: "Meta тимчасово недоступна",
    ownership_or_status_mismatch: "Ціль або її власник не підтверджені",
    parent_mismatch: "Батьківський об’єкт змінився",
    meta_rejected: "Meta відхилила дію",
    unconfirmed_success: "Meta не підтвердила успіх",
    malformed_envelope: "Відповідь Meta не вдалося перевірити",
    malformed_subresponse: "Результат окремої цілі не вдалося перевірити",
    final_fence_changed: "Остання перевірка не дозволила дію",
    expired_send_lease: "Відповідь втрачено — перевіряємо статус",
    missing_result: "Результат відсутній",
    observed_status: "Статус перевірено",
    attempts_exhausted: "Ліміт спроб вичерпано",
    known_success_superseded:
      "Після підтвердженої дії отримано новіший статус об’єкта",
    confirmed_success: "Meta підтвердила вимкнення",
    observed_without_authorship:
      "Статус прочитано після невідомого результату; авторство не підтверджено",
    skipped_parent_action: "Вимкнення батьківської кампанії покриває цю ціль",
    kill_switch: "Глобальну чергу зупинено",
    mutations_disabled: "Meta-дії вимкнено на сервері",
    manual_cancel: "Скасовано користувачем",
    manual_generation_changed:
      "Ручна зміна статусу скасувала попереднє рішення",
    concurrent_manual_or_epoch: "Статус змінився під час запиту",
    configuration_changed: "Правило або призначення змінилося",
    no_current_decision: "Немає актуального підтвердженого рішення",
    decision_expired: "Докази застаріли",
    not_active_at_preflight: "Ціль або її батьківський об’єкт неактивні",
    credentials_or_permission: "Оновіть токен і перевірте права",
    attempts_or_ttl_exhausted: "Ліміт спроб або строк доказів вичерпано",
    transport_unavailable: "Немає відповіді Meta",
    missing_snapshot: "Кабінет ще не завершив синхронізацію",
    incomplete_or_stale_snapshot: "Неповні або застарілі дані",
    unknown_timezone: "Не визначено часовий пояс",
    no_active_eligible_target: "Немає активних цілей",
    unknown_metric: "Метрика невідома (немає даних або знаменника)",
    inactive_target: "Ціль неактивна",
    parent_inactive: "Батьківська кампанія або adset неактивні",
    ambiguous_creative: "Mapping крео потребує перевірки",
    mapping_needs_review: "Зміна крео потребує підтвердження",
    mapping_snapshot_mismatch: "Mapping змінився — потрібна синхронізація",
    unknown_status_or_version_mismatch: "Статус або версія об’єкта змінились",
    stale_or_unknown_status: "Статус застарілий або невідомий",
    empty_scope: "Список порожній",
    inactive_or_unknown_account: "Кабінет неактивний або недоступний",
    stale_partition_after_resolve: "Дані застаріли під час перевірки",
    status_changed_since_evaluation: "Статус змінився після перевірки",
  };
  return labels[key] ?? reason;
}
export function ruleSummary(d: TriggerDefinition) {
  const predicate = (p: Predicate) =>
    metricLabels[p.metric] +
    " " +
    operatorLabels[p.operator].split(" — ")[0] +
    " " +
    p.value;
  return (
    predicate(d.event) +
    (d.conditions
      ? " І (" +
        d.conditions.blocks
          .map(
            (b) =>
              "(" +
              b.predicates
                .map(predicate)
                .join(b.operator === "all" ? " І " : " АБО ") +
              ")",
          )
          .join(d.conditions.operator === "all" ? " І " : " АБО ") +
        ")"
      : "")
  );
}
export function normalizedDefinition(d: TriggerDefinition): TriggerDefinition {
  return {
    ...d,
    event: { ...d.event, value: d.event.value.trim().replace(",", ".") },
    conditions: d.conditions
      ? {
          ...d.conditions,
          blocks: d.conditions.blocks.map((b) => ({
            ...b,
            predicates: b.predicates.map((p) => ({
              ...p,
              value: p.value.trim().replace(",", "."),
            })),
          })),
        }
      : null,
  };
}

export const actionStateLabels: Record<string, string> = {
  dry_run: "Збіг · dry-run",
  queued: "У черзі",
  preflight: "Перевірка активності",
  ready: "Готово до надсилання",
  sending: "Запит надіслано",
  running: "Виконується",
  retry_wait: "Очікує повтору",
  unknown_outcome: "Перевірка невідомого результату",
  reconciling: "Перевірка результату",
  succeeded: "Вимкнення підтверджено",
  partial: "Частковий результат",
  failed: "Помилка",
  failed_terminal: "Спроби завершено",
  blocked_auth: "Перевірте доступ Meta",
  uncertain: "Результат або авторство невідомі",
  cancelled: "Скасовано",
  skipped: "Пропущено",
};
